import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { boApi, inr, ist, num, tone } from "../api";
import { Card, Kpi, Pill, Spinner, PageHead } from "../ui";

const ax = { stroke: "hsl(var(--muted-foreground))", fontSize: 10 };
const tip = { contentStyle: { background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 11 } };

function Chart({ title, data, x, y, kind = "bar", testid }) {
  return (
    <Card title={title} testid={testid}>
      <div className="h-48">
        <ResponsiveContainer>
          {kind === "line" ? (
            <LineChart data={data}><CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" /><XAxis dataKey={x} {...ax} /><YAxis {...ax} allowDecimals={false} /><Tooltip {...tip} /><Line dataKey={y} stroke="hsl(var(--primary))" strokeWidth={2} dot={false} /></LineChart>
          ) : (
            <BarChart data={data}><CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" /><XAxis dataKey={x} {...ax} /><YAxis {...ax} allowDecimals={false} /><Tooltip {...tip} /><Bar dataKey={y} fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} /></BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function MiniList({ title, rows, render, testid }) {
  return (
    <Card title={title} testid={testid}>
      <div className="space-y-1 text-xs">
        {rows.length === 0 && <div className="text-muted-foreground">Nothing yet</div>}
        {rows.map((r, i) => <div key={i} className="flex items-center justify-between gap-3 border-b border-border/50 py-1 last:border-0">{render(r)}</div>)}
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => boApi.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);
  if (!d) return <Spinner className="mx-auto mt-20" />;
  const k = d.kpis;
  const ex = d.market.exchanges || {};
  return (
    <div>
      <PageHead title="Dashboard" sub="Live view of the whole terminal · refreshes every 30s">
        <div data-testid="market-status-widget" className="flex items-center gap-2 text-xs">
          {Object.entries(ex).map(([e, s]) => <span key={e} className="flex items-center gap-1">{e}<Pill value={s} /></span>)}
          <span className="flex items-center gap-1">Feed<Pill value={d.market.health?.toUpperCase()} /></span>
        </div>
      </PageHead>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi testid="kpi-total-clients" label="Total clients" value={num(k.total_clients)} sub={`+${k.new_today} today · +${k.new_week} week · +${k.new_month} month`} />
        <Kpi testid="kpi-traded-today" label="Traded today" value={num(k.traded_today)} />
        <Kpi testid="kpi-online" label="Online now" value={num(k.online_now)} />
        <Kpi testid="kpi-orders" label="Orders today" value={num(k.orders_today)} sub={`${num(k.fills_today)} fills`} />
        <Kpi testid="kpi-rejection" label="Rejection rate" value={`${k.rejection_rate}%`} />
        <Kpi testid="kpi-open-positions" label="Open positions" value={num(k.open_positions)} />
        <Kpi testid="kpi-mtm" label="Live MTM (all clients)" value={inr(k.live_mtm)} toneClass={tone(k.live_mtm)} />
        <Kpi testid="kpi-funds" label="Total virtual funds" value={inr(k.total_funds)} />
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Chart testid="chart-signups" title="New clients per day (30 days)" data={d.charts.signups} x="day" y="n" kind="line" />
        <Chart testid="chart-orders-hour" title="Orders per hour today (IST)" data={d.charts.orders_by_hour} x="hour" y="n" />
        <Chart testid="chart-segments" title="Orders by segment today" data={d.charts.orders_by_segment} x="segment" y="n" />
        <Chart testid="chart-top-symbols" title="Top 10 traded symbols today" data={d.charts.top_symbols} x="symbol" y="n" />
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
        <MiniList testid="list-signups" title="Latest signups" rows={d.lists.signups} render={(u) => <><Link className="hover:text-primary" to={`/admin/clients/${u.id}`}>{u.name}</Link><span className="text-muted-foreground">{ist(u.created_at)}</span></>} />
        <MiniList testid="list-top-pnl" title="Top 10 by today's P&L" rows={d.lists.top_pnl} render={(u) => <><Link className="hover:text-primary" to={`/admin/clients/${u.client_id}`}>{u.name}</Link><span className={`num ${tone(u.pnl)}`}>{inr(u.pnl)}</span></>} />
        <MiniList testid="list-bottom-pnl" title="Bottom 10 by today's P&L" rows={d.lists.bottom_pnl} render={(u) => <><Link className="hover:text-primary" to={`/admin/clients/${u.client_id}`}>{u.name}</Link><span className={`num ${tone(u.pnl)}`}>{inr(u.pnl)}</span></>} />
        <MiniList testid="list-rejected" title="Latest rejected orders" rows={d.lists.rejected} render={(o) => <><Link className="truncate hover:text-primary" to={`/admin/orders/${o.id}`}>{o.side} {o.quantity} {o.symbol} · {o.client_name}</Link><span className="max-w-[45%] truncate text-right text-muted-foreground" title={o.reason}>{o.reason}</span></>} />
      </div>
    </div>
  );
}
